import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import './StreamPlayer.css';

interface Props { streamUrl: string; cameraName: string; isAlert?: boolean; }

function buildWhepUrl(streamUrl: string): string {
  const base = streamUrl.replace('/stream/', '/webrtc/');
  return base.replace(/\/[^/]+\.m3u8.*$/, '/whep');
}

function buildHlsUrl(streamUrl: string): string {
  return streamUrl;
}

async function startWhep(whepUrl: string, video: HTMLVideoElement): Promise<() => void> {
  const pc = new RTCPeerConnection({
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'turn:178.104.154.23:3478', username: 'picam', credential: 'turnpass123' },
    ],
  });
  pc.addTransceiver('video', { direction: 'recvonly' });
  pc.addTransceiver('audio', { direction: 'recvonly' });

  const stream = new MediaStream();
  video.srcObject = stream;

  pc.ontrack = (e) => {
    stream.addTrack(e.track);
    video.play().catch(() => {});
  };

  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);
  await new Promise<void>((resolve) => {
    if (pc.iceGatheringState === 'complete') { resolve(); return; }
    pc.onicegatheringstatechange = () => { if (pc.iceGatheringState === 'complete') resolve(); };
    setTimeout(resolve, 3000);
  });

  const resp = await fetch(whepUrl, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/sdp' },
    body: pc.localDescription!.sdp,
  });

  if (!resp.ok) throw new Error(`WHEP ${resp.status}`);
  const answer = await resp.text();
  await pc.setRemoteDescription({ type: 'answer', sdp: answer });

  return () => pc.close();
}

export default function StreamPlayer({ streamUrl, cameraName, isAlert }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [mode, setMode] = useState<'webrtc' | 'hls'>('webrtc');

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let cleanup: (() => void) | undefined;

    if (mode === 'webrtc') {
      const whepUrl = buildWhepUrl(streamUrl);
      startWhep(whepUrl, video)
        .then((stop) => { cleanup = stop; })
        .catch(() => {});
    } else {
      video.srcObject = null;
      video.src = '';
      const hlsUrl = buildHlsUrl(streamUrl);
      if (Hls.isSupported()) {
        const hls = new Hls({
          lowLatencyMode: true,
          xhrSetup: (xhr: XMLHttpRequest) => { xhr.withCredentials = true; },
        });
        hls.loadSource(hlsUrl);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => video.play().catch(() => {}));
        cleanup = () => hls.destroy();
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = hlsUrl;
      }
    }

    return () => { cleanup?.(); };
  }, [streamUrl, mode]);

  return (
    <div className={`sp-wrap ${isAlert ? 'alert' : ''}`}>
      <div className="sp-header">
        <div className="sp-dot" />
        <span className="sp-name">{cameraName}</span>
        {isAlert && <span className="sp-badge">HƏRƏKƏT</span>}
        <span className="sp-mode" onClick={() => setMode((m: 'webrtc' | 'hls') => m === 'webrtc' ? 'hls' : 'webrtc')} title="Rejimi dəyiş">
          {mode === 'webrtc' ? 'WebRTC' : 'HLS'}
        </span>
      </div>
      <div className="sp-feed">
        <video ref={videoRef} muted playsInline autoPlay className="sp-video" />
        <div className="sp-corner tl" /><div className="sp-corner br" />
        <div className="sp-overlay"><span>1280×720</span><div className="sp-rec"><div className="rec-dot"/>REC</div></div>
      </div>
    </div>
  );
}
